import { RenderPipelineArgs, RenderPipelineArgImageKeysSet } from "@/core/common/types/render-args";
import { RenderAdmissionError } from "./render-admission-request";

const maximumBytes = 6 * 1024 * 1024;
const maximumDataUrlLength = 8 * 1024 * 1024;
const imageKeys = [
  ...RenderPipelineArgImageKeysSet,
  "product_color_correction_image",
  "product_color_correction_mask_image",
];

function invalidImage(): never {
  throw new RenderAdmissionError(
    "This render needs a PNG, JPEG or WebP image that this browser can load.",
  );
}

async function readImage(source: string, signal: AbortSignal) {
  if (/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=\r\n]+$/.test(source)) {
    if (source.length > maximumDataUrlLength) {
      invalidImage();
    }
    return source;
  }
  const url = new URL(source);
  if (
    (url.protocol !== "https:" && !(url.protocol === "blob:" && url.origin === location.origin)) ||
    url.username ||
    url.password ||
    url.hash
  ) {
    invalidImage();
  }
  // The GPU receives bytes; this browser fetch uses no server credential.
  const response = await fetch(url, {
    credentials: "omit",
    redirect: "error",
    cache: "no-store",
    signal,
  });
  const mime = response.headers.get("Content-Type")?.split(";", 1)[0].trim().toLowerCase();
  if (!response.ok || !["image/png", "image/jpeg", "image/webp"].includes(mime) || !response.body) {
    await response.body?.cancel();
    invalidImage();
  }
  const reader = response.body.getReader();
  const chunks: Uint8Array<ArrayBuffer>[] = [];
  let size = 0;
  try {
    let chunk = await reader.read();
    while (!chunk.done) {
      size += chunk.value.byteLength;
      if (size > maximumBytes || signal.aborted) {
        invalidImage();
      }
      // Copy only bounded bytes; no unbounded response.blob()/arrayBuffer() allocation.
      chunks.push(new Uint8Array(chunk.value));
      chunk = await reader.read();
    }
    if (size === 0 || signal.aborted) {
      invalidImage();
    }
    const blob = new Blob(chunks, { type: mime });
    return await new Promise<string>((resolve, reject) => {
      const file = new FileReader();
      const abort = () => file.abort();
      const finish = () => signal.removeEventListener("abort", abort);
      file.onerror = () => {
        finish();
        reject(new RenderAdmissionError("This render image could not be prepared."));
      };
      file.onabort = () => {
        finish();
        reject(new RenderAdmissionError("This render image could not be prepared."));
      };
      file.onload = () => {
        finish();
        if (
          typeof file.result !== "string" ||
          file.result.length > maximumDataUrlLength ||
          signal.aborted
        ) {
          reject(new RenderAdmissionError("This render image is too large or unavailable."));
        } else {
          resolve(file.result);
        }
      };
      signal.addEventListener("abort", abort, { once: true });
      if (signal.aborted) {
        finish();
        reject(new RenderAdmissionError("This render image could not be prepared."));
        return;
      }
      file.readAsDataURL(blob);
    });
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

/** Resolve every supported web image in the caller's browser before creating any paid reservation. */
export async function inlineRenderInputs(args: RenderPipelineArgs): Promise<RenderPipelineArgs> {
  const result: Record<string, unknown> = { ...args };
  const signal = AbortSignal.timeout(30_000);
  const cache = new Map<string, string>();
  let count = 0;
  let totalLength = 0;
  const image = async (value: unknown) => {
    if (value === undefined || value === null || value === "") {
      return value;
    }
    if (typeof value !== "string" || ++count > 12 || value.length > maximumDataUrlLength) {
      invalidImage();
    }
    let data = cache.get(value);
    if (!data) {
      data = await readImage(value, signal);
      cache.set(value, data);
    }
    totalLength += data.length;
    if (totalLength > maximumDataUrlLength) {
      invalidImage();
    }
    return data;
  };
  try {
    for (const key of imageKeys) {
      if (key in result) {
        result[key] = await image(result[key]);
      }
    }
    for (const key of ["canny_image_config", "hed_image_config", "color_image_config"]) {
      if (!(key in result)) {
        continue;
      }
      const entries = result[key];
      if (!Array.isArray(entries) || entries.length > 4) {
        invalidImage();
      }
      const normalized = [];
      for (const entry of entries) {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
          invalidImage();
        }
        normalized.push({ ...entry, image: await image(entry.image) });
      }
      result[key] = normalized;
    }
    if ("image_overlays" in result) {
      const entries = result.image_overlays;
      if (!Array.isArray(entries) || entries.length > 4) {
        invalidImage();
      }
      const normalized = [];
      for (const entry of entries) {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
          invalidImage();
        }
        normalized.push({
          ...entry,
          image: await image(entry.image),
          mask_image: await image(entry.mask_image),
        });
      }
      result.image_overlays = normalized;
    }
    return result as unknown as RenderPipelineArgs;
  } catch (error) {
    if (error instanceof RenderAdmissionError) {
      throw error;
    }
    throw new RenderAdmissionError(
      "This render image could not be loaded. Check its availability before retrying.",
    );
  }
}
